import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { ArrowLeft, LifeBuoy, Send } from 'lucide-react';
import {
  CONTACT_INQUIRY_CATEGORIES,
  CONTACT_INQUIRY_CATEGORY_LABELS,
  type ContactInquiryCategory,
} from '@kulum/shared';
import { db } from '@/lib/firebase';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { Button, Card, CardContent, Input, Select, Textarea } from '@/components/ui';

const MIN_MESSAGE_LENGTH = 10;

export function ContactPage() {
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const addToast = useToastStore((state) => state.addToast);

  const [category, setCategory] = useState<ContactInquiryCategory | ''>('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categoryOptions = useMemo(
    () =>
      CONTACT_INQUIRY_CATEGORIES.map((value) => ({
        value,
        label: CONTACT_INQUIRY_CATEGORY_LABELS[value],
      })),
    []
  );

  const currentEmail = userData?.email || user?.email || '';
  const displayName = userData?.displayName || user?.displayName || '';
  const trimmedMessage = message.trim();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user?.uid) {
      addToast('error', 'ログイン状態を確認できませんでした。再度お試しください。');
      return;
    }

    if (!category) {
      addToast('warning', 'お問い合わせ種別を選択してください。');
      return;
    }

    if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
      addToast('warning', `お問い合わせ内容は${MIN_MESSAGE_LENGTH}文字以上で入力してください。`);
      return;
    }

    try {
      setIsSubmitting(true);
      await addDoc(collection(db, 'contactInquiries'), {
        userId: user.uid,
        userEmail: currentEmail,
        userDisplayName: displayName,
        category,
        message: trimmedMessage,
        status: 'open',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setCategory('');
      setMessage('');
      addToast('success', 'お問い合わせを送信しました。確認し次第、対応します。');
    } catch (error) {
      console.error('Failed to submit contact inquiry:', error);
      addToast('error', 'お問い合わせの送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-full bg-[#F7F4EF]">
      <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
        <div className="px-4 pb-12 pt-14 md:px-0 md:pt-0">
          <Link
            to="/mypage"
            className="mb-5 inline-flex items-center gap-2 text-[13px] text-[#7E7668] transition hover:text-copper"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>マイページに戻る</span>
          </Link>

          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">Support</p>
              <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">お問い合わせ</h1>
              <p className="mt-1 text-sm text-[#8D877D]">
                ご質問、ご要望、不具合のご報告などを受け付けています。
              </p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5EDE2]">
              <LifeBuoy className="h-5 w-5 text-copper" />
            </div>
          </div>

          <Card className="overflow-hidden border-[#ECE6DD]">
            <CardContent className="py-5">
              <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
                <Input label="会員ID" value={user?.uid ?? ''} disabled />
                <Input label="お名前（表示名）" value={displayName} disabled />
                <Input label="メールアドレス" value={currentEmail} disabled />

                <Select
                  label="お問い合わせ種別"
                  value={category}
                  required
                  placeholder="選択してください"
                  options={categoryOptions}
                  onChange={(event) => setCategory(event.target.value as ContactInquiryCategory)}
                />

                <Textarea
                  label="お問い合わせ内容"
                  value={message}
                  required
                  rows={8}
                  minLength={MIN_MESSAGE_LENGTH}
                  placeholder="お問い合わせ内容を具体的にご記入ください。"
                  hint={`最低${MIN_MESSAGE_LENGTH}文字`}
                  onChange={(event) => setMessage(event.target.value)}
                />

                <div className="flex justify-end pt-2">
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || trimmedMessage.length < MIN_MESSAGE_LENGTH || !category}
                    leftIcon={<Send className="h-4 w-4" />}
                    className="!rounded-full"
                  >
                    送信する
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
